import type { ComponentExposed } from 'vue-component-type-helpers'
import type * as Yup from 'yup'

import type LcForm from '#tracking/app/component-overrides/LcForm.vue'

type Form<T extends Record<string, any>> = ComponentExposed<typeof LcForm<T>>

export const useForm = <T extends Record<string, any>>(validationSchema: Yup.ObjectSchema<T>) => {
  const form = ref<Form<T>>()

  const clearErrors = (value: T) => {
    if (!form.value?.internal) return

    const clearedErrors = Object.keys(value).reduce<Record<string, undefined>>((acc, key) => {
      acc[key] = undefined
      return acc
    }, {})
    form.value.internal.setErrors(clearedErrors as object)
  }

  const setFormRef = (formRef: unknown) => {
    form.value = formRef as Form<T>
  }

  const validate = async (value: T) => {
    if (!form.value?.internal) return false

    clearErrors(value)

    try {
      await validationSchema.validate(value, {
        abortEarly: false,
      })
      return true
    } catch (error) {
      const validationErrors: Partial<Record<keyof T, string>> = {}

      ;(error as Yup.ValidationError).inner.forEach((validationError) => {
        const path = validationError.path as keyof T
        return path && (validationErrors[path] = validationError.message)
      })

      form.value.internal.setErrors(validationErrors)
      return false
    }
  }

  return { setFormRef, validate }
}
